import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChessAppLayout } from '@/components/ChessAppLayout';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Download } from 'lucide-react';
import { useTrainingStats } from '@/hooks/useTrainingStats';
import { TrainingStatsOverview } from '@/components/training/TrainingStatsOverview';
import { ImprovementGraph } from '@/components/training/ImprovementGraph';
import { SuccessRateBreakdown } from '@/components/training/SuccessRateBreakdown';
import { TrainingCalendar } from '@/components/training/TrainingCalendar';
import { RecentSessions } from '@/components/training/RecentSessions';
import { TrainingInsights } from '@/components/training/TrainingInsights';
import { useSEO } from '@/hooks/useSEO';

export default function TrainingStats() {
  const navigate = useNavigate();
  const { stats, isLoading, loadStats, getImprovementTrend, getWeakestAreas, getStrongestAreas } = useTrainingStats();

  useSEO({
    title: 'Training Statistics | ChessHatch',
    description: 'Track your chess training progress with detailed statistics, improvement graphs, and personalized insights.',
    keywords: 'chess training stats, chess progress, training analytics, chess improvement'
  });

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handleExport = () => {
    if (!stats) return;
    
    const dataStr = JSON.stringify(stats, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `training-stats-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <ChessAppLayout>
      <div className="container max-w-7xl mx-auto py-8 px-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/training')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Training
            </Button>
            <div>
              <h1 className="text-3xl font-bold">Training Statistics</h1>
              <p className="text-muted-foreground">Your learning journey at a glance</p>
            </div>
          </div>
          {stats && (
            <Button variant="outline" size="sm" onClick={handleExport}>
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          )}
        </div>

        {isLoading ? (
          <div className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {[1, 2, 3, 4].map(i => (
                <Skeleton key={i} className="h-32" />
              ))}
            </div>
            <Skeleton className="h-96" />
            <div className="grid gap-4 md:grid-cols-2">
              <Skeleton className="h-80" />
              <Skeleton className="h-80" />
            </div>
          </div>
        ) : stats ? (
          <div className="space-y-6">
            <TrainingStatsOverview stats={stats} />
            
            <ImprovementGraph stats={stats} />
            
            <SuccessRateBreakdown stats={stats} />
            
            <TrainingCalendar stats={stats} />
            
            <div className="grid gap-4 md:grid-cols-2">
              <RecentSessions stats={stats} />
              <TrainingInsights 
                stats={stats}
                improvementTrend={getImprovementTrend()}
                weakestAreas={getWeakestAreas()}
                strongestAreas={getStrongestAreas()}
              />
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-muted-foreground mb-4">No training data available yet.</p>
            <Button onClick={() => navigate('/training')}>
              Start Training
            </Button>
          </div>
        )}
      </div>
    </ChessAppLayout>
  );
}
